/**
 * calendar-embed — resolve a configured booking URL into the two things a
 * surface can safely do with it: put it in an `<iframe src>`, or put it in an
 * `<a href>`.
 *
 * Both agents that offer a booking now read this:
 *
 *   DemoView          — the post-demo full-bleed calendar + the "Book a call"
 *                       fallback link on the error/wrap-up surfaces.
 *   WebsiteAgentChat  — the in-panel calendar view (permanent button, the
 *                       leave-behind on End, and the bot's `show_calendar`).
 *
 * It lives in its own module because it is a SECURITY check, and a security
 * check with two copies is a security check with one stale copy. DemoView held
 * the only implementation (an inline IIFE pair); the website agent would have
 * been the second, and the copy that never got the next fix — exactly the
 * failure mode CLAUDE.md documents for the accessible snapshot. Pure and
 * DOM-free so `tests/calendar-embed.test.ts` can cover the hostile cases the
 * live surfaces never exercise.
 *
 * Why an ALLOWLIST for the iframe and not for the link:
 *
 *  - An `<iframe src>` renders a third party INSIDE our chrome, where it
 *    inherits the visitor's trust in the surrounding surface. `cal.commerce.com`
 *    and `evil-cal.com.attacker.example.com` both contain "cal.com"; the
 *    original regex (`/(\/\/|^)(cal\.com|calendly\.com)/`) matched both. So the
 *    host is parsed with `URL` and compared by EQUALITY or by a leading-dot
 *    suffix — `app.cal.com` and `<team>.calendly.com` pass, the lookalikes do
 *    not.
 *  - An `<a href>` navigates AWAY, in a new tab, on an explicit click. Any
 *    https origin is fine there; only the scheme needs guarding, because
 *    `javascript:` in an href is script execution on our own page.
 *
 * Adding a host (HubSpot Meetings, Chili Piper, Google appointment schedules…)
 * is a one-line change to {@link EMBEDDABLE_HOSTS} — but verify it first:
 * a provider that serves `X-Frame-Options: DENY` renders a permanently blank
 * box, which is strictly worse than the link fallback this returns today.
 */

/**
 * Hosts we are willing to render inside our own chrome. Each entry matches the
 * bare host AND its subdomains (`app.cal.com`, `acme.calendly.com`) — never a
 * host that merely ENDS in the string (`evil-cal.com`), because the suffix test
 * includes the separating dot.
 */
export const EMBEDDABLE_HOSTS: readonly string[] = ['cal.com', 'calendly.com', 'schedulehero.io'];

export interface CalendarEmbed {
  /**
   * The URL to put in an `<iframe src>`, or null when the configured link is
   * absent, malformed, not https, contains embedded credentials, or is not a
   * known-embeddable host. Null does NOT mean "no calendar" — check
   * `fallbackHref` before concluding that.
   */
  embedSrc: string | null;
  /**
   * The URL to put in an `<a href target="_blank">`, or null when the
   * configured link is absent, malformed, not https, or contains embedded
   * credentials. Non-null whenever `embedSrc` is, so a surface can render the
   * embed and still offer "open in a new tab" from the same resolution.
   */
  fallbackHref: string | null;
}

/** Nothing configured / nothing usable. A frozen singleton so callers can
 *  compare identity in a memo dep without allocating per render. */
const NOTHING: CalendarEmbed = Object.freeze({ embedSrc: null, fallbackHref: null });

/** True when `host` is one of {@link EMBEDDABLE_HOSTS} or a subdomain of one. */
export function isEmbeddableHost(host: string): boolean {
  const h = host.toLowerCase();
  return EMBEDDABLE_HOSTS.some((allowed) => h === allowed || h.endsWith(`.${allowed}`));
}

/**
 * Resolve a configured booking URL. Never throws — a malformed or hostile value
 * degrades to `{embedSrc: null, fallbackHref: null}`, which every caller renders
 * as "no booking offered" rather than as a broken control.
 */
export function resolveCalendarEmbed(url: string | null | undefined): CalendarEmbed {
  if (typeof url !== 'string') return NOTHING;
  const raw = url.trim();
  if (!raw) return NOTHING;

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    // Relative or garbage — `new URL` throws without a base. A relative
    // booking URL is meaningless anyway: the overlay runs on the CUSTOMER's
    // page, so it would resolve against their marketing site, not ours.
    return NOTHING;
  }

  // http: is deliberately refused, not downgraded to link-only. The overlay is
  // embedded on https pages; a mixed-content iframe is blocked by the browser
  // and a plaintext booking link leaks the prospect's prefilled email.
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password) {
    return NOTHING;
  }

  // Schedule Hero advertises this mode in its oEmbed/iframely link. Keep the
  // standalone URL for the fallback so opening a tab retains the full page.
  const scheduleHero = parsed.hostname === 'schedulehero.io' || parsed.hostname.endsWith('.schedulehero.io');
  if (scheduleHero) parsed.searchParams.set('embed', 'true');
  return {
    embedSrc: isEmbeddableHost(parsed.hostname) ? (scheduleHero ? parsed.toString() : raw) : null,
    fallbackHref: raw,
  };
}

/** True when there is SOMETHING to offer — an embed or at least a link. */
export function hasCalendar(url: string | null | undefined): boolean {
  return resolveCalendarEmbed(url).fallbackHref !== null;
}
